import React from 'react';
import { Heart, Instagram, Facebook, Youtube, Moon, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAccessibility } from '../hooks/useAccessibility';
import logoBranca from '../assets/logo_branca.png';
import logoAzul from '../assets/logo_azul.webp';

/** variant="reading": mesma ideia do Header — só a tela do artigo do blog usa. */
const Footer = ({ variant = 'default' }: { variant?: 'default' | 'reading' }) => {
  const { speak, isDarkMode, toggleDarkMode } = useAccessibility();

  // "Equipe" removida da navegação de propósito — página (/equipe) continua
  // existindo em App.tsx, só não tem mais link visível daqui.
  const sobreLinks = [
    { name: 'Recursos do App', href: '/recursos' },
    { name: 'Blog', href: '/blog' },
  ];

  const suporteLinks = [
    { name: 'Perguntas Frequentes', href: '/faq' },
    { name: 'Central de Suporte', href: '/suporte' },
    { name: 'Fale Conosco', href: '/contato' },
  ];

  const acessoLinks = [
    { name: 'Entrar', href: '/login' },
    { name: 'Painel', href: '/dashboard' },
  ];

  const instituicoes = ['GPTICAM', 'IFAP', 'CAPES', 'LIARED', 'Inova Educação'];

  const redesSociais = [
    { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/gpticam/' },
    { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/GPTICAM' },
    { name: 'YouTube', icon: Youtube, href: 'https://www.youtube.com/@Conectar-y9o' },
  ];

  const linkClass = 'block text-sm text-muted-foreground hover:text-primary transition-colors';
  const headingClass = 'text-sm font-semibold text-foreground';

  return (
    <footer className={variant === 'reading' ? 'reading-nav border-t' : 'bg-background border-t border-border'}>
      <div className="container mx-auto px-4 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-12">
          {/* Marca */}
          <div className="max-w-xs space-y-3">
            <Link to="/" className="flex items-center space-x-2">
              <img
                src={isDarkMode ? logoBranca : logoAzul}
                alt="CONECTAR Logo"
                className="h-8 w-auto object-contain"
              />
              <span className="text-lg font-bold text-foreground">CONECTAR</span>
            </Link>
            <p className="font-body text-sm text-muted-foreground leading-relaxed">
              Tecnologia que entende, escuta e acolhe. Um app criado com afeto para apoiar
              crianças autistas e suas famílias.
            </p>
            <div className="flex items-center space-x-2 text-primary">
              <Heart className="w-4 h-4" />
              <span className="text-xs font-medium">Feito com amor e inclusão</span>
            </div>
          </div>

          {/* Colunas de links */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-10">
            <div className="space-y-3">
              <h3 className={headingClass}>Sobre</h3>
              <nav className="space-y-2.5">
                {sobreLinks.map((link) => (
                  <Link key={link.name} to={link.href} className={linkClass} onClick={() => speak(link.name)}>
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="space-y-3">
              <h3 className={headingClass}>Suporte</h3>
              <nav className="space-y-2.5">
                {suporteLinks.map((link) => (
                  <Link key={link.name} to={link.href} className={linkClass} onClick={() => speak(link.name)}>
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="space-y-3">
              <h3 className={headingClass}>Instituições</h3>
              <ul className="space-y-2.5">
                {instituicoes.map((nome) => (
                  <li key={nome} className="text-sm text-muted-foreground">
                    {nome}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className={headingClass}>Acesso</h3>
              <nav className="space-y-2.5">
                {acessoLinks.map((link) => (
                  <Link key={link.name} to={link.href} className={linkClass} onClick={() => speak(link.name)}>
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Barra inferior */}
        <div className="border-t border-border mt-12 pt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-xs">
              © 2026 CONECTAR. Todos os direitos reservados.
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  toggleDarkMode();
                  speak(isDarkMode ? 'Modo claro ativado' : 'Modo escuro ativado');
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 text-foreground transition-colors"
                aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="h-3.5 w-3.5 text-primary" />
                    Modo claro
                  </>
                ) : (
                  <>
                    <Moon className="h-3.5 w-3.5 text-primary" />
                    Modo escuro
                  </>
                )}
              </button>

              <div className="flex items-center gap-2">
                {redesSociais.map((rede) => (
                  <a
                    key={rede.name}
                    href={rede.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={rede.name}
                    className="w-8 h-8 rounded-lg bg-secondary hover:bg-primary hover:text-primary-foreground text-foreground flex items-center justify-center transition-colors"
                    onClick={() => speak(rede.name)}
                  >
                    <rede.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
